import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ImagePlus, Trash, Sparkles, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isHeicFile, convertHeicToJpeg } from '../utils/heicConvert';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import { postGenerateAd, GenerateAdError } from '../lib/generateAdRequest';
import { resizeImageIfNeeded } from '../utils/imageResize';
import { preloadGeneratedImages } from '../utils/preloadGeneratedImages';
import LoadingAnimation from '../components/LoadingAnimation';

export const AddFirstProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productName, setProductName] = useState('');
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);
  const [productImageFile, setProductImageFile] = useState<File | null>(null);
  const [generating, setGenerating] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState(false);
  const [showInstructionsPopup, setShowInstructionsPopup] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const consecutiveErrorCountRef = useRef<number>(0);
  const [showContactQR, setShowContactQR] = useState<boolean>(false);
  const [userRubro, setUserRubro] = useState<string>('Moda e Indumentaria');
  const willShowResultsRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prevent browser back button from returning to /onboarding
  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Auto-dismiss generation error after 5 seconds
  useEffect(() => {
    if (!genError) return;
    const t = setTimeout(() => setGenError(null), 5000);
    return () => clearTimeout(t);
  }, [genError]);

  // Fetch user's rubro on mount
  useEffect(() => {
    const fetchRubro = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();

        if (!(profile as any)?.id_company) return;

        const { data: company } = await supabase
          .from('company_profiles')
          .select('rubro')
          .eq('id_company', (profile as any).id_company)
          .single();

        if (company?.rubro) {
          setUserRubro(company.rubro as string);
        }
      } catch (err) {
        // Keep default rubro on error
      }
    };
    fetchRubro();
  }, []);

  // Determine which images and instructions to show based on rubro
  const isFashion = userRubro === 'Moda e Indumentaria';
  const isHomeDecor = userRubro === 'Hogar y Decoración';
  
  const getExampleImages = () => {
    if (isFashion) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto siendo utilizado'
      };
    } else if (isHomeDecor) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    } else {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    }
  };
  
  const exampleImages = getExampleImages();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validate file size (max 100MB)
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
    if (file.size > MAX_FILE_SIZE) {
      setError('El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Check if the file is a HEIC image and convert to JPEG
      let processedFile = file;
      let processedUrl = '';
      
      if (isHeicFile(file)) {
        try {
          const converted = await convertHeicToJpeg(file, 0.8);
          processedFile = converted.file;
          processedUrl = converted.url;
        } catch (err) {
          setError('No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg');
          return;
        }
      } else {
        processedUrl = URL.createObjectURL(file);
      }

      // Resize image if too large (max 2000px)
      processedFile = await resizeImageIfNeeded(processedFile, 2000, 0.85);
      processedUrl = URL.createObjectURL(processedFile);

      // Store file for later upload and create preview URL
      setProductImageFile(processedFile);
      setProductImageUrl(processedUrl);

    } catch (err) {
      console.error('Error processing image:', err);
      setError(err instanceof Error ? err.message : 'Error al procesar la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setProductImageUrl(null);
    setProductImageFile(null);
  };

  const handleGenerateImage = async () => {
    if (!productImageFile) return;

    // Auto-fill product name if empty
    if (!productName.trim()) {
      setProductName('Producto 1');
    }
    const finalProductName = productName.trim() || 'Producto 1';

    try {
      setLoading(true);
      setGenerating(true);
      setCompleteProgressNow(false);
      setError(null);

      // 1) Get user and company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No se encontró la empresa del usuario');
      const idCompany = (profile as any).id_company as string;

      // 2) Create product
      const { data: producto, error: productoError } = await supabase
        .from('productos')
        .insert({ id_company: idCompany, nombre: finalProductName, tiene_variantes: false })
        .select('id')
        .single();
      if (productoError) throw productoError;

      const productId = String(producto.id);

      // 3) Create variant for that product
      const { data: variant, error: variantError } = await supabase
        .from('variantes')
        .insert({ id_company: idCompany, nombre: finalProductName, activo: true, product_id: productId })
        .select('id')
        .single();
      if (variantError) throw variantError;

      const variantId = variant.id as string;

      // 4) Upload image to productos bucket - sanitize filename
      const timestamp = Date.now();
      const originalName = productImageFile.name;
      const sanitizedName = originalName.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileBaseName = `${timestamp}_${sanitizedName}`;

      // Upload full image
      const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
      const { error: fullErr } = await supabase.storage.from('productos').upload(fullPath, productImageFile);
      if (fullErr) throw fullErr;

      // Create optimized thumbnail
      try {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        const blob = await new Promise<Blob>((resolve, reject) => {
          img.onload = () => {
            const target = 300;
            let { width, height } = img;
            if (width > height) {
              if (width > target) {
                height = Math.round(height * (target / width));
                width = target;
              }
            } else if (height > target) {
              width = Math.round(width * (target / height));
              height = target;
            }
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Blob failed')), 'image/jpeg', 0.8);
          };
          img.onerror = () => reject(new Error('Image load error'));
          img.src = URL.createObjectURL(productImageFile);
        });

        const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
        await supabase.storage.from('productos').upload(optPath, blob);
      } catch (optErr) {
        console.warn('Error creating optimized image, continuing', optErr);
      }

      // 5) Insert image record in DB
      const { error: imageError } = await supabase
        .from('productos_imagenes')
        .insert({ id_company: idCompany, variante_id: variantId, url: fileBaseName, principal: true, orden: 1 });
      if (imageError) throw imageError;

      // 6) Notify categorization webhook (fire and forget)
      try {
        await fetch('https://ai-agency-dt7d.onrender.com/webhook/categorizar_producto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
          body: JSON.stringify({ user_id: user.id, product_id: productId })
        });
      } catch {}

      // 7) Generate image with automatic mode and model
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: [variantId],
        mode: 'single',
        modo: 'rapido',
        estilo: 'automatico',
        tipo: 'imagen',
        cantidad_imagenes: 2,
        Format: '4:5',
        composicion: 'modelo',
        modo_pro: true,
        new_ui: true
      };

      console.log('[AddFirstProduct] handleGenerateImage payload', requestData);
      const data: any = await postGenerateAd(requestData);
      console.log('[AddFirstProduct] handleGenerateImage response', data);

      if (Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || idCompany;

        // Preload images (with polling + opt/ creation) before navigating — same as PruebaNuevaInterfaz
        let preloadedImages: { id: number; fileName: string; signedUrl: string; optSignedUrl?: string; favorito: boolean }[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(imageIds, companyIdResult);
        } catch (preloadErr) {
          console.warn('[AddFirstProduct] Image preload failed, continuing anyway:', preloadErr);
        }

        setCompleteProgressNow(true);
        
        // Navigate to generated images page with preloaded data + lastPayload for "Generar más"
        navigate('/redes-sociales/imagenes-generadas', {
          replace: true,
          state: { imageIds, companyId: companyIdResult, lastPayload: requestData, preloadedImages, isOnboarding: true }
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }

    } catch (err) {
      console.error('Error generating image:', err);
      if (err instanceof GenerateAdError && err.isActionable) {
        setGenError(err.message);
      } else {
        consecutiveErrorCountRef.current += 1;
        setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }
      setGenerating(false);
      setCompleteProgressNow(false);
    } finally {
      setLoading(false);
    }
  };

  const canGenerate = productImageFile !== null;

  return (
    <>
      {/* Loading Animation - 75 seconds (1 min 15 seg) */}
      {generating && (
        <LoadingAnimation
          message="Generando tu primera imagen..."
          subMessage="Esto puede tomar hasta un minuto"
          durationMs={75000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            if (!willShowResultsRef.current) {
              setGenerating(false);
            }
          }}
        />
      )}

      {/* Floating error toast — matching PruebaNuevaInterfaz */}
      {genError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 z-[9999] pointer-events-none" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div
            className="pointer-events-auto mx-4 sm:mx-0 w-[calc(100vw-2rem)] sm:w-[380px] bg-white rounded-[13px] shadow-[0_8px_32px_rgba(0,0,0,0.18),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4"
          >
            <div className="flex items-start gap-3">
              <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">error</span>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-brand-black/90">Error al generar</p>
                <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">{genError}</p>
                {consecutiveErrorCountRef.current >= 2 && (
                  <p className="text-[13px] text-[#3C3C43]/60 mt-1">
                    Si el problema persiste,{' '}
                    <a
                      href="https://wa.me/5491122635392?text=Hola%2C%20tuve%20un%20error%20al%20generar%20im%C3%A1genes%20en%20Atlas"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1644EB] underline sm:hidden"
                    >
                      contactanos
                    </a>
                    <button
                      type="button"
                      onClick={() => setShowContactQR(true)}
                      className="text-[#1644EB] underline hidden sm:inline"
                    >
                      contactanos
                    </button>
                  </p>
                )}
              </div>
              <button type="button" onClick={() => setGenError(null)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="min-h-screen bg-white md:bg-surface-bg flex items-center justify-center p-4 md:bg-cover md:bg-center md:bg-no-repeat"
        style={{ backgroundImage: 'var(--onboarding-bg)' }}
      >
        <style>{`@media (min-width: 768px) { :root { --onboarding-bg: url('https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/fondo_onboarding_compu.jpg'); } }`}</style>
        <div className="w-full max-w-md md:max-w-xl">
          <div className="bg-white md:bg-surface-card rounded-none md:rounded-lg shadow-none md:shadow-sm px-6 md:p-8">
            <h2 className="text-[30px] md:text-[22px] font-semibold text-brand-black/90 mb-2 leading-tight">
              Agregá tu primer producto
            </h2>
            <p className="text-[15px] text-neutral-400 mb-8">
              Subí una foto de tu producto y generá tu primera imagen con IA
            </p>

            {error && (
              <div className="mb-5 p-3 bg-error-soft border border-error/20 rounded-md text-[13px] text-error-text flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <div className="space-y-6">
              {/* Product Name */}
              <div>
                <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
                  Nombre del producto
                </label>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Ej: Remera básica negra"
                  className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                />
              </div>

              {/* Product Image */}
              <div>
                <label className="text-[13px] font-medium text-neutral-600 mb-2 block">
                  Foto del producto
                </label>
                {productImageUrl ? (
                  <div className="relative w-40 h-40 rounded-md overflow-hidden border border-neutral-200">
                    <img 
                      src={productImageUrl} 
                      alt="Product" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1.5 bg-surface-card rounded-sm shadow-md hover:bg-error-soft transition-colors"
                    >
                      <Trash className="w-3.5 h-3.5 text-error" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="file"
                      id="product-image-upload"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <button
                      type="button"
                      onClick={() => setShowInstructionsPopup(true)}
                      className={`flex items-center md:justify-center gap-2 h-[var(--btn-h)] px-5 border border-neutral-200 rounded-md cursor-pointer hover:bg-surface-elevated transition-colors w-full md:w-auto text-[15px] font-medium text-brand-black/90 ${
                        uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-5 h-5" />
                          Subir foto
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="pt-4 flex md:justify-end">
                <button
                  type="button"
                  onClick={handleGenerateImage}
                  disabled={!canGenerate || loading}
                  className="w-full md:w-auto h-[var(--btn-h)] px-8 bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 shadow-sm font-medium text-[15px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generar imagen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Popup */}
      {showInstructionsPopup && (
        <div className="fixed inset-0 bg-brand-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card w-full max-w-md rounded-lg shadow-lg p-6 relative">
            <button
              type="button"
              onClick={() => setShowInstructionsPopup(false)}
              className="absolute top-3 right-3 p-2 text-neutral-400 hover:text-neutral-600 rounded-sm focus:outline-none transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-4">
              Cómo subir una buena foto
            </h3>
            
            {/* Example images */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                <div className="w-full aspect-square rounded-md border border-neutral-200 bg-surface-card overflow-hidden relative">
                  <img
                    src={exampleImages.img1}
                    alt={exampleImages.alt1}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                  <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                <div className="w-full aspect-square rounded-md border border-neutral-200 bg-surface-card overflow-hidden relative">
                  <img
                    src={exampleImages.img2}
                    alt={exampleImages.alt2}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                  <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </div>
            
            {/* Instructions */}
            <ul className="text-[15px] text-neutral-600 list-disc pl-4 space-y-2 mb-6">
              <li>Subí una foto en donde se vea claramente el producto que querés vender.</li>
              {isFashion && <li>Fotos del producto siendo utilizado (sin mostrar el rostro) funcionan mejor.</li>}
            </ul>
            
            <button
              type="button"
              onClick={() => {
                setShowInstructionsPopup(false);
                fileInputRef.current?.click();
              }}
              className="w-full h-[var(--btn-h)] bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Contact QR Modal - Desktop only */}
      {showContactQR && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6 relative">
            <button
              onClick={() => setShowContactQR(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-neutral-900 mb-2 text-center">Contactanos por WhatsApp</h3>
            <p className="text-[15px] text-neutral-600 mb-4 text-center">Escaneá el código QR con tu celular para abrir WhatsApp</p>
            <div className="flex justify-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://wa.me/5491122635392?text=Hola%2C%20tuve%20un%20error%20al%20generar%20im%C3%A1genes%20en%20Atlas')}`}
                alt="QR Code WhatsApp"
                className="w-48 h-48"
              />
            </div>
            <button
              onClick={() => setShowContactQR(false)}
              className="w-full py-2 px-4 bg-neutral-100 text-neutral-700 rounded-full hover:bg-neutral-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddFirstProduct;
